/**
 * The keyboard of a select-only combobox (WAI-ARIA APG), kept apart from the
 * component so it can be tested without a DOM. Focus never leaves the trigger:
 * `active` is the option `aria-activedescendant` names while the list is open.
 */
export type ListboxState = {
  open: boolean;
  active: number;
  /** The typeahead search so far, and when its last letter came. */
  typed: string;
  typedAt: number;
};

export const closed: ListboxState = { open: false, active: 0, typed: '', typedAt: 0 };

/** The APG's pause: a letter after this long starts a new search. */
export const TYPEAHEAD_MS = 500;

export type KeyResult = {
  state: ListboxState;
  /** Whether the key was consumed, so the caller should `preventDefault`. */
  handled: boolean;
  choose?: number;
};

/** Case and accents aside, so "ti" finds "Tiếng Việt". */
const fold = (s: string) => s.normalize('NFD').replace(/\p{M}/gu, '').toLocaleLowerCase();

/** The first option from `from` on whose label starts with `prefix`, wrapping; `null` if none does. */
function search(labels: readonly string[], from: number, prefix: string): number | null {
  const wanted = fold(prefix);
  for (let step = 0; step < labels.length; step++) {
    const i = (from + step) % labels.length;
    if (fold(labels[i]!).startsWith(wanted)) return i;
  }
  return null;
}

const isLetter = (key: string) => key.length === 1 && key !== ' ';

/**
 * A repeated single letter cycles from the option after `current`; anything
 * longer extends the search and keeps `current` while it still matches.
 */
function typeahead(state: ListboxState, key: string, current: number, labels: readonly string[], now: number) {
  const typed = now - state.typedAt < TYPEAHEAD_MS ? state.typed + key : key;
  const repeated = typed.split('').every((c) => c === typed[0]);
  const found = repeated ? search(labels, current + 1, typed[0]!) : search(labels, current, typed);
  return { found, typed };
}

export function onKey(
  state: ListboxState,
  key: string,
  { labels, selected, now, disabled }: { labels: readonly string[]; selected: number; now: number; disabled?: boolean },
): KeyResult {
  const last = labels.length - 1;
  const at = (active: number): KeyResult => ({ state: { ...closed, open: true, active }, handled: true });

  if (!state.open) {
    if (disabled) return { state: closed, handled: false };
    if (key === 'Enter' || key === ' ' || key === 'ArrowDown' || key === 'ArrowUp') return at(Math.max(selected, 0));
    if (key === 'Home') return at(0);
    if (key === 'End') return at(last);
    if (isLetter(key)) {
      const { found, typed } = typeahead(state, key, selected, labels, now);
      if (found !== null) return { state: { open: true, active: found, typed, typedAt: now }, handled: true };
    }
    return { state: closed, handled: false };
  }

  switch (key) {
    case 'ArrowDown':
      return at(Math.min(state.active + 1, last));
    case 'ArrowUp':
      return at(Math.max(state.active - 1, 0));
    case 'Home':
      return at(0);
    case 'End':
      return at(last);
    case 'Enter':
    case ' ':
      return { state: closed, handled: true, choose: state.active };
    case 'Escape':
      return { state: closed, handled: true };
    case 'Tab':
      return { state: closed, handled: false };
  }
  if (isLetter(key)) {
    const { found, typed } = typeahead(state, key, state.active, labels, now);
    return { state: { open: true, active: found ?? state.active, typed, typedAt: now }, handled: true };
  }
  return { state, handled: false };
}
